import React from "react";
import { CircleAlert, ExternalLink, Trash2 } from "lucide-react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { TERMS_URL } from "../../../../config/runtime";

import loginLogo from "../../../../assets/page/login_logo.svg";
import loginCircleLeft from "../../../../assets/page/login_circle_left.svg";
import loginCircleRight from "../../../../assets/page/login_circle_right.svg";

const HELP_DESK_URL = "https://helpdesk.civicpoints.io/en/customer/create-ticket/";

export const DeleteAccountPage: React.FC = () => {
  const { t } = useTranslation();

  return (
    <div className="relative min-h-screen overflow-hidden bg-white text-[#0f172a]">
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute inset-x-0 top-0 h-[64vh] bg-gradient-to-b from-[#f8fbfa] via-white to-white" />
        <img
          src={loginCircleLeft}
          alt=""
          aria-hidden="true"
          className="absolute bottom-16 left-0 w-[220px] opacity-35"
        />
        <img
          src={loginCircleRight}
          alt=""
          aria-hidden="true"
          className="absolute bottom-16 right-0 w-[220px] opacity-35"
        />
      </div>

      <header className="relative z-10 flex items-center justify-between px-6 py-5 md:px-8 md:py-7">
        <Link
          to="/login"
          className="shrink-0"
        >
          <img
            src={loginLogo}
            alt="CivicPoints"
            className="h-7 w-auto md:h-9"
          />
        </Link>
        <Link
          to="/login"
          className="rounded-full px-2 py-1 text-[15px] font-semibold text-[#0b5c43] transition-colors hover:text-[#00a57f]"
        >
          {t("auth.login")}
        </Link>
      </header>

      <main className="relative z-10 flex min-h-[calc(100vh-88px)] flex-col items-center justify-center px-5 pb-10 pt-8 md:px-8">
        <section className="w-full max-w-[600px] rounded-[26px] border border-[#e7ebf1] bg-white px-6 py-10 shadow-[0_24px_70px_rgba(15,23,42,0.10)] md:px-12 md:py-14">
          <div className="mx-auto mb-7 flex h-[118px] w-[118px] items-center justify-center rounded-full bg-[#fff1f1]">
            <Trash2
              className="h-14 w-14 text-[#c24141]"
              strokeWidth={1.8}
            />
          </div>

          <div className="text-center">
            <h1 className="[font-family:'Cal_Sans-Regular',Helvetica] text-[30px] font-bold leading-tight text-[#0b5c43] md:text-[38px]">
              {t("auth.delete_account_title")}
            </h1>
            <p className="mx-auto mt-4 max-w-[450px] text-[16px] leading-7 text-[#6f7686] md:text-[17px]">
              {t("auth.delete_account_description")}
            </p>
          </div>

          <div className="mt-8 rounded-2xl border border-[#f2d3d3] bg-[#fff8f8] p-5 text-left">
            <div className="flex gap-3">
              <CircleAlert
                className="mt-0.5 h-5 w-5 shrink-0 text-[#c24141]"
                aria-hidden="true"
              />
              <p className="text-[15px] leading-6 text-[#6f4b4b]">
                {t("auth.delete_account_notice")}
              </p>
            </div>
          </div>

          <a
            href={HELP_DESK_URL}
            className="mt-8 flex w-full items-center justify-center gap-2 rounded-xl bg-[#c24141] px-5 py-3.5 text-[16px] font-semibold text-white transition-colors hover:bg-[#a93434] focus:outline-none focus:ring-2 focus:ring-[#c24141] focus:ring-offset-2"
          >
            {t("auth.delete_account_action")}
            <ExternalLink
              className="h-4 w-4"
              aria-hidden="true"
            />
          </a>

          <p className="mt-5 text-center text-[14px] leading-6 text-[#7a8192]">
            {t("auth.delete_account_help")}
          </p>
        </section>

        <footer className="mt-14 text-center text-[14px] text-[#7a8192] md:mt-16">
          <a
            href={TERMS_URL}
            className="transition-colors hover:text-[#0b5c43]"
          >
            {t("auth.footer_terms")}
          </a>
          <span className="mx-3 text-[#c5cad4]">|</span>
          <a
            href="https://civicpoints.io/privacy-policy"
            className="transition-colors hover:text-[#0b5c43]"
          >
            {t("auth.footer_privacy")}
          </a>
          <span className="mx-3 text-[#c5cad4]">|</span>
          <a
            href={HELP_DESK_URL}
            className="transition-colors hover:text-[#0b5c43]"
          >
            {t("auth.footer_help")}
          </a>
        </footer>
      </main>
    </div>
  );
};
